import { ApolloClient } from "@apollo/client";
import { NormalizedCacheObject } from "@apollo/client";
import { getAuth } from "@clerk/nextjs/server";
import { GetServerSidePropsContext, NextPage } from "next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import FullContainerLoader from "@/components/common/loaders/fullContainerLoader";
import { useAnalytics } from "@/components/common/segment/AnalyticsContext";
import CustomBookingThemeProvider from "@/config/customBookingThemeProvider";
import { StripeProvider } from "@/config/stripe";
import { ClientBookingContextProvider } from "@/contexts/clientBookingContext";
import {
  MedspaAdditionalPublicInfoDocument,
  MedspaAdditionalPublicInfoQueryResult,
  MedspaInfoDocument,
  MedspaInfoQueryResult,
} from "@/graphql/queries/medspaInfo.graphql.types";
import {
  MedspaInfoWithoutExclusiveCustomBookingLinksServicesDocument,
  MedspaInfoWithoutExclusiveCustomBookingLinksServicesQueryResult,
} from "@/graphql/queries/medspaInfoWithoutExclusiveCustomBookingLinksServices.graphql.types";
import { SelectiveBookingLinkQueryResult } from "@/graphql/queries/onlineBooking/selectiveBookingLinks.graphql.types";
import { SelectiveBookingLinkDocument } from "@/graphql/queries/onlineBooking/selectiveBookingLinks.graphql.types";
import { MedspaOnlineStoreDataDocument } from "@/graphql/queries/onlineStore/medspaOnlineStoreData.graphql.types";
import { MedspaOnlineStoreDataQueryResult } from "@/graphql/queries/onlineStore/medspaOnlineStoreData.graphql.types";
import { useEffectOnce } from "@/hooks/misc/useEffectOnce";
import { addApolloState, initializeApollo } from "@/lib/apollo/apollo";
import { calculateBookingCustomizationEnabled } from "@/utils";
import { getDefaultBookingState } from "@/utils/booking";
import { FeatureFlags } from "@/utils/featureFlags";
import { MedspaOnlineStoreData } from "@/views/online-store/onlineStoreView";

type MedspaInfoType =
  | MedspaInfoQueryResult["data"]["medspa"][0]
  | MedspaInfoWithoutExclusiveCustomBookingLinksServicesQueryResult["data"]["medspa"][0];

type BookingPageProps = {
  medspaInfo: MedspaInfoType;
  defaultState: ReturnType<typeof getDefaultBookingState>;
  packagesInfo: MedspaOnlineStoreData["packages"] | null;
  selectiveBookingLinkData: SelectiveBookingLinkQueryResult["data"] | null;
};

type FeatureFlagsConfig = {
  isExclusiveToCustomBookingLinksV1Enabled: boolean;
};

async function fetchFeatureFlagsConfiguration(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  medspaSlug: string
): Promise<FeatureFlagsConfig> {
  const { data: medspaPublicInfo } = await apolloClient.query<
    MedspaAdditionalPublicInfoQueryResult["data"]
  >({
    query: MedspaAdditionalPublicInfoDocument,
    variables: { slug: medspaSlug },
    context: { noAuth: true },
  });

  const enabledFeatures =
    medspaPublicInfo?.medspa?.[0]?.additionalPublicInfo?.enabledFeatures ?? [];

  return {
    isExclusiveToCustomBookingLinksV1Enabled: enabledFeatures.includes(
      FeatureFlags.EXCLUSIVE_TO_CUSTOM_BOOKING_LINKS_V1
    ),
  };
}

async function fetchMedspaInformation(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  medspaSlug: string,
  hasSelectiveBookingLink: boolean,
  isExclusiveToCustomBookingLinksV1Enabled: boolean
): Promise<MedspaInfoType> {
  try {
    // Query selection logic based on feature flag and booking link type:
    //
    // MedspaInfoWithoutExclusiveCustomBookingLinksServicesQuery:
    // - Only used when EXCLUSIVE_TO_CUSTOM_BOOKING_LINKS_V1 feature flag is enabled
    // - Excludes "exclusive services" (services only available for custom booking links)
    // - Used for regular booking scenarios to ignore exclusive services
    //
    // MedspaInfoQuery:
    // - Used when feature flag is disabled OR when it's a custom booking link
    // - Includes ALL services (including exclusive ones)
    // - Booking page logic will handle filtering based on service availability
    if (isExclusiveToCustomBookingLinksV1Enabled && !hasSelectiveBookingLink) {
      const { data } = await apolloClient.query<
        MedspaInfoWithoutExclusiveCustomBookingLinksServicesQueryResult["data"]
      >({
        query: MedspaInfoWithoutExclusiveCustomBookingLinksServicesDocument,
        variables: { slug: medspaSlug },
        context: { noAuth: true },
      });

      if (!data.medspa.length) {
        throw new Error("Medspa not found");
      }

      return data.medspa[0];
    }

    // Fetch all services (including exclusive ones) for custom booking links
    // or when feature flag is disabled
    const { data } = await apolloClient.query<MedspaInfoQueryResult["data"]>({
      query: MedspaInfoDocument,
      variables: { slug: medspaSlug },
      context: { noAuth: true },
    });

    if (!data.medspa.length) {
      throw new Error("Medspa not found");
    }

    return data.medspa[0];
  } catch (error) {
    console.error("Error fetching medspa information:", error);
    throw error;
  }
}

async function fetchOptionalData(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  medspaSlug: string,
  selectiveBookingLinkId: string | string[] | undefined
) {
  const [packagesData, selectiveBookingLinkData] = await Promise.all([
    fetchPackagesData(apolloClient, medspaSlug),
    selectiveBookingLinkId
      ? fetchSelectiveBookingLinkData(
          apolloClient,
          selectiveBookingLinkId as string
        )
      : Promise.resolve(null),
  ]);

  return { packagesData, selectiveBookingLinkData };
}

async function fetchPackagesData(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  medspaSlug: string
) {
  const { data: onlineStoreData } = await apolloClient.query<
    MedspaOnlineStoreDataQueryResult["data"]
  >({
    query: MedspaOnlineStoreDataDocument,
    variables: { slug: medspaSlug },
    context: { noAuth: true },
  });

  return onlineStoreData?.medspa[0].packages ?? null;
}

async function fetchSelectiveBookingLinkData(
  apolloClient: ApolloClient<NormalizedCacheObject>,
  selectiveBookingLinkId: string
) {
  const { data: selectiveBookingLink } = await apolloClient.query<
    SelectiveBookingLinkQueryResult["data"]
  >({
    query: SelectiveBookingLinkDocument,
    variables: { id: selectiveBookingLinkId },
    context: { noAuth: true },
  });

  return selectiveBookingLink;
}

function enhanceMedspaConfiguration(
  medspaInfo: MedspaInfoType
): MedspaInfoType {
  const isBookingCustomizationEnabled =
    medspaInfo.configuration?.groupServiceMenuItemsByCategoriesEnabled;

  const enhancedMedspaInfo = structuredClone(medspaInfo);
  enhancedMedspaInfo.configuration = {
    ...medspaInfo.configuration,
    groupServiceMenuItemsByCategoriesEnabled: isBookingCustomizationEnabled,
  };

  return enhancedMedspaInfo;
}

const BookingView = dynamic(() => import("@/views/booking/bookingView"), {
  ssr: false,
  loading: () => <FullContainerLoader />,
});

const Booking: NextPage<BookingPageProps> = ({
  medspaInfo,
  defaultState,
  packagesInfo,
  selectiveBookingLinkData,
}) => {
  const analytics = useAnalytics();
  const isCustomizationEnabled =
    calculateBookingCustomizationEnabled(medspaInfo);
  const { customizationPrimaryColor } = medspaInfo.configuration;
  const { pathname } = useRouter();

  useEffectOnce(!!analytics, () => {
    analytics.page(
      "",
      { moxiePath: pathname },
      { context: { groupId: medspaInfo.id } }
    );
  });

  return (
    <CustomBookingThemeProvider
      customizationEnabled={isCustomizationEnabled}
      customizationPrimaryColor={customizationPrimaryColor}
    >
      <ClientBookingContextProvider
        medspaData={medspaInfo}
        defaultState={defaultState}
        packagesInfo={packagesInfo}
        selectiveBookingLinkData={selectiveBookingLinkData}
      >
        <StripeProvider>
          <BookingView />
        </StripeProvider>
      </ClientBookingContextProvider>
    </CustomBookingThemeProvider>
  );
};

Booking.disablePageTracking = true;
export default Booking;

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  try {
    const { slug: medspaSlug, selectiveBookingLinkId } = ctx.query;
    const { getToken: getClerkToken } = getAuth(ctx.req);
    const apolloClient = initializeApollo({ getClerkToken });
    const hasSelectiveBookingLink = !!selectiveBookingLinkId;

    // Step 1: Fetch feature flags configuration
    const featureFlagsConfig = await fetchFeatureFlagsConfiguration(
      apolloClient,
      medspaSlug as string
    );

    // Step 2: Fetch medspa information based on feature flags
    const medspaInfo = await fetchMedspaInformation(
      apolloClient,
      medspaSlug as string,
      hasSelectiveBookingLink,
      featureFlagsConfig.isExclusiveToCustomBookingLinksV1Enabled
    );

    // Step 3: Fetch optional data (packages and selective booking link)
    const { packagesData, selectiveBookingLinkData } = await fetchOptionalData(
      apolloClient,
      medspaSlug as string,
      selectiveBookingLinkId
    );

    // Step 4: Process and enhance medspa configuration
    const enhancedMedspaInfo = enhanceMedspaConfiguration(medspaInfo);

    return addApolloState(apolloClient, {
      props: {
        medspaInfo: enhancedMedspaInfo,
        defaultState: getDefaultBookingState(medspaInfo, ctx.query),
        packagesInfo: packagesData,
        selectiveBookingLinkData,
      },
    });
  } catch (error) {
    console.error("Error in getServerSideProps:", error);

    return { notFound: true };
  }
};
